import { forwardRef } from 'react';

import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { Flex } from '../../layouts/flex/Flex.js';

//#region Typings

/**
 * Accepted properties for the Menu Suffix.
 * @public
 */
export interface MenuSuffixProps
  extends WithChildren, AriaLabelingProps, StylingProps, DataTestId {}
//#endregion

/**
 * The `Menu.Suffix` component allows you to specify any content that should
 * be suffixed in the menu item. The `Menu.Suffix` component is only valid
 * within the `Menu.Item`, and `Menu.Link` elements.
 * @example
 * ```tsx
 * <Menu.Item onSelect={() => {}}>
 *   Label
 *   <Menu.Suffix>
 *     <WhatsNewIcon />
 *   </Menu.Suffix>
 * </Menu.Item>
 * ```
 * @public
 */
export const Suffix = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuSuffixProps
>(
  (
    {
      children,
      className,
      style,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
    },
    forwardedRef,
  ) => {
    return (
      <Flex
        ref={forwardedRef}
        className={className}
        style={style}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
      >
        {children}
      </Flex>
    );
  },
);

(Suffix as typeof Suffix & { displayName: string }).displayName = 'Menu.Suffix';
